import { Card, Colors, Heading, Separator, type IconName } from '@amelio/ui-library';

import TipItem from '../TipItem/TipItem';

import { useAmelioTranslations } from '@/hooks';
import i18n from '@/plugins/i18n';

interface Tip {
    icon: IconName;
    title: string;
    text: string;
    highlightWord?: string;
}

export interface ClassNameProps {
    className?: string;
    isSelfEvaluation: boolean;
}

/**
 * StartReviewContent
 *
 * Displays preparation tips before starting a 360 Evaluation.
 * Pure UI component.
 */
export const StartReviewContent = ({ isSelfEvaluation, className = '' }: ClassNameProps) => {
    const { t } = useAmelioTranslations();
    const currentLanguage = i18n.language;

    const growthHighlightWord = currentLanguage === 'fr' ? 'développement' : 'growth,';

    /**
     * Static tips configuration.
     * Icons marked TODO will be updated once available in the library.
     */
    const TIPS: Tip[] = [
        {
            icon: 'notifications-notification-circle',
            title: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.calm.title'
                    : 'startReview.content.calm.title',
            ),
            text: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.calm.text'
                    : 'startReview.content.calm.text',
            ),
        },
        {
            icon: 'search-search-status',
            title: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.examples.title'
                    : 'startReview.content.examples.title',
            ),
            text: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.examples.text'
                    : 'startReview.content.examples.text',
            ),
        },
        {
            icon: 'security-eye',
            title: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.beyondRole.title'
                    : 'startReview.content.beyondRole.title',
            ),
            text: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.beyondRole.text'
                    : 'startReview.content.beyondRole.text',
            ),
        },
        {
            icon: 'business-diagram',
            title: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.growth.title'
                    : 'startReview.content.growth.title',
            ),
            text: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.growth.text'
                    : 'startReview.content.growth.text',
            ),
            highlightWord: growthHighlightWord,
        },
        {
            icon: 'location-gps',
            title: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.experience.title'
                    : 'startReview.content.experience.title',
            ),
            text: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.experience.text'
                    : 'startReview.content.experience.text',
            ),
        },
        {
            icon: 'character-pharagraphspacing',
            title: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.balance.title'
                    : 'startReview.content.balance.title',
            ),
            text: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.balance.text'
                    : 'startReview.content.balance.text',
            ),
        },
        {
            icon: 'notifications-lamp-charge',
            title: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.unsure.title'
                    : 'startReview.content.unsure.title',
            ),
            text: t(
                isSelfEvaluation
                    ? 'startReview.selfEvaluationContent.unsure.text'
                    : 'startReview.content.unsure.text',
            ),
        },
    ];
    const cardClasses = `rounded-3xl bg-white ${className}`.trim();

    return (
        <Card
            className={cardClasses}
            padding={'md'}
        >
            <Heading
                level={2}
                weight={'semibold'}
                color={Colors.BASE}
                className={'mb-16 max-w-3xl text-2xl'}
            >
                {t(
                    isSelfEvaluation
                        ? 'startReview.selfEvaluationContent.cardTitle'
                        : 'startReview.content.cardTitle',
                )}

                <br />

                {t(
                    isSelfEvaluation
                        ? 'startReview.selfEvaluationContent.cardText'
                        : 'startReview.content.cardText',
                )}
            </Heading>

            <ul className={'bg-white p-5 md:!p-10'}>
                {/* First tip */}
                <li className={'mb-10'}>
                    <TipItem {...TIPS[0]} />
                </li>

                <Separator className={'my-4 h-px w-full md:my-10'} />

                {/* Tips 2–3 */}
                <li className={'grid gap-12 md:grid-cols-2'}>
                    {TIPS.slice(1, 3).map((tip) => (
                        <TipItem
                            key={tip.title}
                            {...tip}
                        />
                    ))}
                </li>

                <Separator className={'my-4 h-px w-full md:my-10'} />

                {/* Tips 4–5 */}
                <li className={'grid gap-12 md:grid-cols-2'}>
                    {TIPS.slice(3, 5).map((tip) => (
                        <TipItem
                            key={tip.title}
                            {...tip}
                        />
                    ))}
                </li>

                <Separator className={'my-4 h-px w-full md:my-10'} />

                {/* Tips 6–7 */}
                <li className={'grid gap-12 md:grid-cols-2'}>
                    {TIPS.slice(5, 7).map((tip) => (
                        <TipItem
                            key={tip.title}
                            {...tip}
                        />
                    ))}
                </li>
            </ul>
        </Card>
    );
};
